import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { CloseSquare, Maximize4 } from 'iconsax-reactjs';
import { cn } from '@/lib/utils';
import { useI18n } from '@/lib/i18n';
import { useFillHeight } from '@/lib/useFillHeight';

interface ChartFrameProps {
  children: ReactNode;
  /** Classes for the normal, in-page state — height, border, background. */
  className?: string;
  /** Names the chart in the button's accessible label. */
  label: string;
  /**
   * Controls to surface INSIDE the frame while full screen.
   *
   * Anything in the page toolbar is unreachable once the chart covers the
   * viewport, so settings you would want to change while reading the chart have
   * to come with it. Rendered only when full screen, so the same control is not
   * shown twice in the normal layout.
   */
  fullscreenToolbar?: ReactNode;
  /**
   * Whether the frame grows to fill the window below it.
   *
   * On by default, which is right for a page with one chart. A page stacking
   * several charts turns it off and sizes each with its className, since three
   * frames each claiming the rest of the window would push the second and
   * third off the bottom.
   */
  fill?: boolean;
}

/**
 * Wraps a chart and gives it a full-screen toggle.
 *
 * Uses the native Fullscreen API where it exists, because that is the only way
 * to reclaim the browser's own chrome — which is most of what you gain on a
 * laptop. Where it does not exist, or is refused, it falls back to pinning the
 * frame over the viewport, which looks the same inside the page and simply
 * leaves the browser UI in place.
 *
 * The charts autosize from a ResizeObserver on their container, so neither path
 * needs to tell them anything: growing the frame is enough.
 */
export function ChartFrame({
  children,
  className,
  label,
  fullscreenToolbar,
  fill = true,
}: ChartFrameProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [full, setFull] = useState(false);
  const { t } = useI18n();
  const height = useFillHeight(ref, fill && !full);

  // Track the browser's own state, so leaving full screen with Escape or the
  // system UI keeps the button and the styling in step.
  useEffect(() => {
    const onChange = () => {
      const el = ref.current;
      if (!el) return;
      if (document.fullscreenElement === el) setFull(true);
      else if (document.fullscreenElement === null) setFull((prev) => (isNative() ? false : prev));
    };
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  // Escape only ends native full screen by itself; the fallback has to listen.
  useEffect(() => {
    if (!full || isNative()) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFull(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [full]);

  const toggle = useCallback(() => {
    const el = ref.current;
    if (!el) return;

    if (document.fullscreenElement === el) {
      void document.exitFullscreen();
      return;
    }

    if (el.requestFullscreen) {
      // A rejected request still has to leave the UI usable, so fall back to
      // the pinned frame rather than doing nothing.
      el.requestFullscreen().catch(() => setFull(true));
      return;
    }
    setFull((prev) => !prev);
  }, []);

  return (
    <div
      ref={ref}
      // Fills the window below wherever the frame sits; see useFillHeight.
      // Inline so it wins over the page's fixed h-[..] class, which stays as
      // the fallback before the first measurement.
      style={!full && height !== null ? { height } : undefined}
      className={cn(
        // w-full + min-w-0 keep the frame from propagating its full-screen width
        // back into the page layout on exit.
        'relative w-full min-w-0',
        full
          ? // bg-background matters: a fullscreened element is painted on black
            // by default, and the chart draws on a transparent background.
            'fixed inset-0 z-50 bg-background p-2'
          : className,
      )}
    >
      {children}

      {full && fullscreenToolbar && (
        <div className="absolute start-2 top-2 z-10 flex items-center gap-1 rounded-lg bg-elevated/80 px-2 py-1.5 backdrop-blur-sm">
          {fullscreenToolbar}
        </div>
      )}

      <button
        type="button"
        onClick={toggle}
        aria-label={full ? t('common.exitFullScreen') : t('common.chartFullScreen', { name: label })}
        title={full ? t('common.exitFullScreen') : t('common.fullScreen')}
        className={cn(
          'absolute end-2 top-2 z-10 flex size-8 items-center justify-center rounded-lg',
          'bg-elevated/80 text-muted-foreground backdrop-blur-sm transition-colors',
          'hover:text-foreground focus-visible:opacity-100',
          // Out of the way until wanted, but always reachable by keyboard.
          full ? 'opacity-100' : 'opacity-0 focus-visible:opacity-100 group-hover/frame:opacity-100',
        )}
      >
        {full ? <CloseSquare size={16} /> : <Maximize4 size={16} />}
      </button>
    </div>
  );
}

/** Whether the browser offers the real thing. */
function isNative(): boolean {
  return typeof document !== 'undefined' && document.fullscreenEnabled === true;
}
